import { Effect, Reducer } from '@umijs/max';
import {
  getFilesPKIs,
  getFilesByType,
  getFilesByReceptor,
  getFilesByEmployee,
  getFilesTopBenefits,
  getFilesTopLocations,
  getFilesTopAllowances,
  getFilesTopBenefitsMonthly,
  getFileLifestampByBenefits,
  getFilesTopBenefitsByImport,
  getTransferTypeAccumulation,
  getFileLifestampByAllowance,
  getTransferTypeAverageDelay,
  getFilesTopAllowancesMonthly,
  getFilesTopAllowancesByImport,
} from '@/services/files.service';

import { AnalysisData } from './data';

export interface ModelType {
  namespace: string;
  state: AnalysisData;
  effects: {
    fetchPKIs: Effect;
    fetchFilesByType: Effect;
    fetchFilesLifespan: Effect;
    fetchFileByEmployee: Effect;
    fetchFileByReceptor: Effect;
    fetchFileTopBenefits: Effect;
    fetchFileTopLocations: Effect;
    fetchFilesTopAllowances: Effect;
    fetchTransferTypeAverageDelay: Effect;
    fetchTransferTypeAccumulation: Effect;
  };
  reducers: {
    save: Reducer<AnalysisData>;
    clear: Reducer<AnalysisData>;
  };
}

const initState = {
  filePKIs: null as any,
  filesByType: null as any,
  filesByReceptor: null as any,
  filesByEmployee: null as any,
  fileTopLocations: null as any,
  fileTopAllowances: null as any,
  fileTopBenefitsImport: null as any,
  fileLifestampByBenefit: null as any,
  fileTopBenefitsMonthly: null as any,
  fileTopAllowancesImport: null as any,
  fileTopAllowancesMonthly: null as any,
  transferTypeAverageDelay: null as any,
  transferTypeAccumulation: null as any,
  fileLifestampByAllowance: null as any,
};

const Model: ModelType = {
  namespace: 'fileReports',

  state: initState,

  effects: {
    *fetchPKIs(_, { call, put }) {
      const { data: filePKIs } = yield call(getFilesPKIs);
      yield put({
        type: 'save',
        payload: { filePKIs },
      });
    },

    *fetchFilesLifespan({ payload }, { all, call, put }) {
      yield put({
        type: 'save',
        payload: { fileLifestampByAllowance: null, fileLifestampByBenefit: null },
      });
      const [allowanceRes, benefitRes] = yield all([
        call(getFileLifestampByAllowance, payload),
        call(getFileLifestampByBenefits, payload),
      ]);

      yield put({
        type: 'save',
        payload: {
          fileLifestampByAllowance: allowanceRes.data,
          fileLifestampByBenefit: benefitRes.data,
        },
      });
    },

    *fetchFilesByType({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: { filesByType: null },
      });
      const { data: filesByType } = yield call(getFilesByType, payload);
      yield put({
        type: 'save',
        payload: { filesByType },
      });
    },

    *fetchFileTopLocations({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: { fileTopLocations: null },
      });
      const { data: fileTopLocations } = yield call(getFilesTopLocations, payload);
      yield put({
        type: 'save',
        payload: { fileTopLocations },
      });
    },

    *fetchFileTopBenefits({ payload }, { all, call, put }) {
      yield put({
        type: 'save',
        payload: {
          fileTopBenefits: null,
          fileTopBenefitsImport: null,
          fileTopBenefitsMonthly: null,
        },
      });
      const [benefitsRes, benefitsImportRes, benefitsMonthlyRes] = yield all([
        call(getFilesTopBenefits, payload),
        call(getFilesTopBenefitsByImport, payload),
        call(getFilesTopBenefitsMonthly, payload),
      ]);
      yield put({
        type: 'save',
        payload: {
          fileTopBenefits: benefitsRes.data,
          fileTopBenefitsImport: benefitsImportRes.data,
          fileTopBenefitsMonthly: benefitsMonthlyRes.data,
        },
      });
    },

    *fetchFileByEmployee({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: { filesByEmployee: null },
      });
      const { data: filesByEmployee } = yield call(getFilesByEmployee, payload);
      yield put({
        type: 'save',
        payload: { filesByEmployee },
      });
    },

    *fetchFileByReceptor({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: { filesByReceptor: null },
      });
      const { data: filesByReceptor } = yield call(getFilesByReceptor, payload);
      yield put({
        type: 'save',
        payload: { filesByReceptor },
      });
    },

    *fetchFilesTopAllowances({ payload }, { all, call, put }) {
      yield put({
        type: 'save',
        payload: {
          fileTopAllowances: null,
          fileTopAllowancesImport: null,
          fileTopAllowancesMonthly: null,
        },
      });
      const [allowancesRes, allowancesMonthlyRes, allowancesImportRes] = yield all([
        call(getFilesTopAllowances, payload),
        call(getFilesTopAllowancesMonthly, payload),
        call(getFilesTopAllowancesByImport, payload),
      ]);
      yield put({
        type: 'save',
        payload: {
          fileTopAllowances: allowancesRes.data,
          fileTopAllowancesMonthly: allowancesMonthlyRes.data,
          fileTopAllowancesImport: allowancesImportRes.data,
        },
      });
    },

    *fetchTransferTypeAverageDelay({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: { transferTypeAverageDelay: null },
      });
      const { data: transferTypeAverageDelay } = yield call(getTransferTypeAverageDelay, payload);
      yield put({
        type: 'save',
        payload: { transferTypeAverageDelay },
      });
    },

    *fetchTransferTypeAccumulation({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: { transferTypeAccumulation: null },
      });
      const { data: transferTypeAccumulation } = yield call(getTransferTypeAccumulation, payload);
      yield put({
        type: 'save',
        payload: { transferTypeAccumulation },
      });
    },
  },

  reducers: {
    save(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    clear() {
      return initState;
    },
  },
};

export default Model;
